import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftIcon, MailIcon, MessageCircleIcon, ShieldCheckIcon } from "lucide-react";
import { company } from "../../i18n/translations";
import { LEGAL_EMAIL, type LegalPageContent } from "../../i18n/legal";
interface LegalPageProps {
  content: LegalPageContent;
  showWhatsapp?: boolean;
}

// Shared layout for every public legal page: privacy, terms and data deletion.
export function LegalPage({
  content,
  showWhatsapp = false
}: LegalPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [content.title]);
  return <main data-id="element-57" data-mp="325b24qbbpzt2">
      {/* Compact navy header — legal pages need clarity, not photography. */}
      <section className="w-full bg-navy pt-32 pb-14" data-id="element-58" data-mp="2x79nqioueonx">
        <div className="max-w-content mx-auto px-4 sm:px-6" data-id="element-59" data-mp="2x8knm7f63c9r">
          <span className="inline-flex items-center gap-2 text-orange font-semibold text-xs sm:text-sm uppercase tracking-[0.18em]" data-id="element-60" data-mp="2s6llf0v5ylee">
            <span className="h-px w-6 bg-orange" aria-hidden="true" data-id="element-61" data-mp="2n9bxxqrc4jt7" />
            {content.eyebrow}
          </span>
          <h1 className="mt-3 font-heading font-bold uppercase text-white text-4xl sm:text-5xl leading-[1.02]" data-id="element-62" data-mp="2h07sokkj1o4y">
            {content.title}
          </h1>
          <p className="mt-4 text-sm text-slate-400" data-id="element-63" data-mp="274xte4lijuuv">{content.updated}</p>
        </div>
      </section>

      <section className="w-full bg-white py-14 sm:py-20" data-id="element-64" data-mp="2c3r4jqdbv9rv">
        <div className="max-w-content mx-auto px-4 sm:px-6" data-id="element-65" data-mp="2zstlew050ylb">
          <div className="max-w-3xl" data-id="element-66" data-mp="2ztbmc5xqw0zk">
            <p className="border-l-4 border-orange pl-5 text-base sm:text-lg text-slate-600 leading-relaxed" data-id="element-67" data-mp="34rd6yvatk8d5">
              {content.intro}
            </p>

            <div className="mt-12 space-y-10" data-id="element-68" data-mp="2pu4dnw1yguis">
              {content.sections.map(section => <article key={section.title} data-id="element-69" data-mp="2urmeez5tk3fp">
                  <h2 className="font-heading font-bold uppercase text-navy text-xl sm:text-2xl" data-id="element-70" data-mp="1eepfioy0fwm3">
                    {section.title}
                  </h2>
                  <p className="mt-3 text-slate-600 leading-relaxed" data-id="element-71" data-mp="1jc7epnm86xgw">{section.body}</p>
                  {section.items && <ul className="mt-4 space-y-2.5" data-id="element-72" data-mp="3h24i3et6abo8">
                      {section.items.map(item => <li key={item} className="flex gap-3 text-slate-600 leading-relaxed" data-id="element-73" data-mp="3camgcojbs727">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-orange" aria-hidden="true" data-id="element-74" data-mp="37ccoamswdmkt" />
                          <span data-id="element-75" data-mp="2ybee1mrv8its">{item}</span>
                        </li>)}
                    </ul>}
                </article>)}
            </div>

            {/* Contact block for exercising data rights */}
            <div className="mt-14 rounded-sm border border-slate-200 bg-slate-50 p-6 sm:p-8" data-id="element-76" data-mp="2oerur2f6tztv">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-navy text-orange" data-id="element-77" data-mp="rjlxcal9lbhv">
                <ShieldCheckIcon size={26} data-id="element-78" data-mp="mmn5jyp2pgmu" />
              </span>
              <h2 className="mt-5 font-heading font-bold uppercase text-navy text-xl sm:text-2xl" data-id="element-79" data-mp="u4bmq8yqm8h7">
                {content.contactTitle}
              </h2>
              <p className="mt-2 text-slate-600 leading-relaxed" data-id="element-80" data-mp="z23gl8iy04o1">{content.contactIntro}</p>
              <ul className="mt-5 space-y-3" data-id="element-81" data-mp="13xio7iqcx4c0">
                <li data-id="element-82" data-mp="18uiyk5u8ot8x">
                  <a href={`mailto:${LEGAL_EMAIL}`} className="inline-flex items-center gap-2.5 font-medium text-navy transition-colors hover:text-orange break-all" data-id="element-83" data-mp="18tpxnlhyoxyp">
                    <MailIcon size={18} className="shrink-0 text-orange" aria-hidden="true" data-id="element-84" data-mp="g7ofndd2m1xk" />
                    {LEGAL_EMAIL}
                  </a>
                </li>
                {showWhatsapp && <li data-id="element-85" data-mp="q1f0m89znu8i">
                    <a href={company.whatsappLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2.5 font-medium text-navy transition-colors hover:text-orange" data-id="element-86" data-mp="1wkd74b7izvnd">
                      <MessageCircleIcon size={18} className="shrink-0 text-orange" aria-hidden="true" data-id="element-87" data-mp="1v36fd1mrgzaq" />
                      WhatsApp — “ELIMINAR MIS DATOS”
                    </a>
                  </li>}
              </ul>
              <p className="mt-5 text-sm text-slate-500" data-id="element-88" data-mp="2i1t5ev306jzy">{company.legalName}</p>
            </div>

            <Link to="/" className="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-orange transition-colors hover:text-orange-dark" data-id="element-89" data-mp="2e0r488pizxvc">
              <ArrowLeftIcon size={17} data-id="element-90" data-mp="2mumbpne4e0e5" />
              {content.backHome}
            </Link>
          </div>
        </div>
      </section>
    </main>;
}